<script setup lang="ts">
import { chevronDown, addOutline } from "ionicons/icons";
import {
  IonContent,
  IonPage,
  IonGrid,
  IonSearchbar,
  IonRadioGroup,
  IonRadio,
  IonPopover,
  IonRow,
  IonProgressBar,
  IonLabel,
  IonItem,
  IonFab,
  IonFabButton,
  IonCol,
  IonButton,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  onIonViewWillEnter,
} from "@ionic/vue";
import { useAppStore } from "@/stores/app";
import { useContextStore } from "@/stores/context";
import { useVisitorsStore } from "@/stores/visitors";
import { computed, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import { IVisitor, IVisitorTypeChoice } from "@/models/visitor";
import { IUserCard } from "@/views/_partials";
import UserCard from "@/views/_partials/UserCard.vue";
import { getVisitorTypeChoices } from "@/api/visitors";
import EventSidebar from "@/views/_partials/EventSidebar.vue";
import { useWindowWidth } from "@/composition/window";

const { isMobile } = useWindowWidth();
const appStore = useAppStore();
const contextStore = useContextStore();
const visitorsStore = useVisitorsStore();
const visitorTypeChoices = ref([] as IVisitorTypeChoice[]);
const route = useRoute();

const dark_mode = computed(() => appStore.settings.theme === "dark");

const state = reactive({
  empty: false,
  filter_selected: "",
});

const form = {
  search: "",
  visitor_type: "",
  checkin_status: "",
};

const inital_checkin_status = route.params.checkin_status as string;

const params = {
  search: "",
  visitor_type: "",
  checkin_status: "",
  visitor_status: "registered",
  event: contextStore.event.id,
  page: 1,
  page_size: 10,
  reset: false,
};

const more = () => {
  params.page = params.page + 1;
  params.reset = false;
  visitorsStore.fetch(params);
};

const search = () => {
  params.search = form.search;
  params.page = 1;
  params.reset = true;
  visitorsStore.fetch(params);
};

const cardConfig = (visitor: IVisitor) => {
  const config: IUserCard = {
    name: visitor.name,
    detail: visitor.email,
    picture: visitor.profile_picture,
    checkin_status: visitor.checkin_status,
  };
  return config;
};

const handleRefresh = async (event: any) => {
  params.checkin_status = "";
  state.filter_selected = "registered";

  await visitorsStore.fetch({ ...params, reset: true });
  await getVisitorTypeChoices({ event: contextStore.event.id }).then(
    (response) => {
      visitorTypeChoices.value = response.data.results;
    }
  );
  if (event.target) {
    event.target.complete();
  }
};

const filterByStatus = async (status: string) => {
  params.checkin_status = status;

  await visitorsStore.fetch({ ...params, reset: true }).then(() => {
    if (visitorsStore.results.length === 0) {
      state.empty = true;
    } else {
      state.empty = false;
    }
  });
  getVisitorTypeChoices({ event: contextStore.event.id }).then((response) => {
    visitorTypeChoices.value = response.data.results;
  });
};

onIonViewWillEnter(() => {
  params.page = 1;

  state.filter_selected = inital_checkin_status ? inital_checkin_status : "";
  params.checkin_status = inital_checkin_status ? inital_checkin_status : "";

  visitorsStore.fetch({ ...params, reset: true }).then(() => {
    if (visitorsStore.results.length === 0) {
      state.empty = true;
    } else {
      state.empty = false;
    }
  });
  getVisitorTypeChoices({ event: contextStore.event.id }).then((response) => {
    visitorTypeChoices.value = response.data.results;
  });
});
</script>
<template>
  <ion-page>
    <ion-content>
      <ion-refresher
        v-if="isMobile"
        slot="fixed"
        @ionRefresh="handleRefresh($event)"
      >
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <div class="flex-desktop">
        <event-sidebar v-if="!isMobile" :event="contextStore.event" />
        <div :class="dark_mode ? 'full-width' : 'colored-bg full-width'">
          <div class="container">
            <h1 id="visitors-filter">
              All visitors
              <ion-icon
                :icon="chevronDown"
                v-if="visitorTypeChoices.length > 0"
              ></ion-icon>
            </h1>
            <ion-popover
              reference="trigger"
              trigger="visitors-filter"
              side="bottom"
              alignment="start"
              trigger-action="click"
              v-if="visitorTypeChoices.length > 0"
            >
              <ion-content>
                <ion-radio-group value="all">
                  <ion-item
                    v-for="visitorTypeChoice in visitorTypeChoices"
                    :key="`visitor-type-${visitorTypeChoice.id}`"
                  >
                    <ion-label>{{ visitorTypeChoice.name }}</ion-label>
                    <ion-radio
                      :value="visitorTypeChoice.visitor_type"
                    ></ion-radio>
                  </ion-item>
                </ion-radio-group>
              </ion-content>
            </ion-popover>
            <ion-grid v-if="isMobile">
              <ion-row>
                <ion-col size="6">
                  <p>Attendance status</p>
                </ion-col>
                <ion-col size="3">
                  <small
                    >{{ contextStore.event.checked_in_visitors }} of
                    {{ contextStore.event.registered_visitors }}</small
                  >
                </ion-col>
                <ion-col>
                  <ion-progress-bar
                    style="margin-top: 0.6rem"
                    :value="
                      contextStore.event.checked_in_visitors /
                      contextStore.event.registered_visitors
                    "
                  ></ion-progress-bar>
                </ion-col>
              </ion-row>
            </ion-grid>
            <div
              v-else
              class="flex-desktop"
              style="justify-content: space-between"
            >
              <div class="flex-desktop full-width">
                <div style="margin-right: 30px">
                  <p>Attendance status</p>
                </div>
                <div style="margin-right: 10px">
                  <small
                    >{{ contextStore.event.checked_in_visitors }} of
                    {{ contextStore.event.registered_visitors }}</small
                  >
                </div>
                <div style="width: 20%">
                  <ion-progress-bar
                    style="margin-top: 0.6rem"
                    :value="
                      contextStore.event.checked_in_visitors /
                      contextStore.event.registered_visitors
                    "
                  ></ion-progress-bar>
                </div>
              </div>
              <div style="position: relative; bottom: 45px">
                <ion-fab-button
                  color="primary"
                  :router-link="`/event/${contextStore.event.id}/add-visitors`"
                >
                  <ion-icon :icon="addOutline"></ion-icon>
                </ion-fab-button>
              </div>
            </div>

            <ion-item
              class="ion-margin-top ion-margin-bottom"
              v-if="!state.empty"
            >
              <ion-searchbar
                placeholder="Search visitors"
                @change="search()"
                v-model="form.search"
              ></ion-searchbar>
            </ion-item>
            <ion-grid>
              <!-- USE THIS FOR ADDITIONAL FILTERS -->
              <ion-row class="">
                <ion-col>
                  <p>Visitors</p>
                </ion-col>
                <ion-col>
                  <p class="ion-text-right" id="click-trigger">
                    Status <ion-icon :icon="chevronDown"></ion-icon>
                  </p>
                  <ion-popover
                    reference="trigger"
                    trigger="click-trigger"
                    side="bottom"
                    alignment="start"
                    trigger-action="click"
                    :dismiss-on-select="true"
                  >
                    <ion-content>
                      <ion-radio-group v-model="state.filter_selected">
                        <ion-item>
                          <ion-label>All Visitors</ion-label>
                          <ion-radio
                            @click="filterByStatus('')"
                            value=""
                          ></ion-radio>
                        </ion-item>
                        <ion-item>
                          <ion-label>Registered</ion-label>
                          <ion-radio
                            @click="filterByStatus('registered')"
                            value="registered"
                          ></ion-radio>
                        </ion-item>
                        <ion-item>
                          <ion-label>Entered</ion-label>
                          <ion-radio
                            @click="filterByStatus('checkedin')"
                            value="checkedin"
                          ></ion-radio>
                        </ion-item>
                        <ion-item>
                          <ion-label>Checked out</ion-label>
                          <ion-radio
                            @click="filterByStatus('checkedout')"
                            value="checkedout"
                          ></ion-radio>
                        </ion-item>
                      </ion-radio-group>
                    </ion-content>
                  </ion-popover>
                </ion-col>
              </ion-row>
            </ion-grid>

            <UserCard
              :user="cardConfig(visitor)"
              :route="`/event/${contextStore.event.id}/visitor/${visitor.id}`"
              v-for="(visitor, index) in visitorsStore.results"
              :key="`visitor-${index}`"
            />
            <template v-if="visitorsStore.loading">
              <UserCard
                v-for="(empty, index) in new Array(5)"
                :key="`visitor-${index}`"
              />
            </template>
            <ion-button
              expand="block"
              color="dark"
              fill="clear"
              @click="more()"
              style="margin: 1rem 0"
              v-if="visitorsStore.results.length < visitorsStore.item_count"
            >
              See more
            </ion-button>
            <figure class="image" v-if="state.empty && !visitorsStore.loading">
              <img
                src="@/assets/undraw_no_data.svg"
                alt=""
                style="width: 50vw; margin: 4rem auto 0; display: block"
              />
              <hr />
              <p class="ion-text-center">Nobody registered yet!</p>
            </figure>
          </div>
        </div>
      </div>
    </ion-content>
    <ion-fab
      v-if="isMobile"
      vertical="bottom"
      horizontal="end"
      slot="fixed"
      style="margin-right: 0.5rem"
    >
      <ion-fab-button
        color="primary"
        :router-link="`/event/${contextStore.event.id}/add-visitors`"
      >
        <ion-icon :icon="addOutline"></ion-icon>
      </ion-fab-button>
    </ion-fab>
  </ion-page>
</template>

<style scoped lang="scss">
@media (min-width: 768px) {
  .image {
    margin: 0 auto;
    width: 300px;
  }

  .colored-bg.full-width {
    overflow-y: auto;
    height: calc(100vh - 72px);
  }
}
</style>
